import React from "react";

interface BudgetComparisonProps {
  fourYearTotal?: number; // Total budget for 4-year term in baht
  currentYearBudget?: number; // Current year budget in baht for comparison
}

// Format number with commas
const formatNumberWithCommas = (num: number): string => {
  return num.toLocaleString("th-TH");
};

// Truck icon SVG
const TruckIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2" />
    <path d="M15 18H9" />
    <path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14" />
    <circle cx="17" cy="18" r="2" />
    <circle cx="7" cy="18" r="2" />
  </svg>
);

// Road icon SVG
const RoadIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M4 19L8 5" />
    <path d="M16 5l4 14" />
    <path d="M12 8v2" />
    <path d="M12 14v2" />
  </svg>
);

// Utensils icon SVG for school lunch
const UtensilsIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2" />
    <path d="M7 2v20" />
    <path d="M21 15V2v0a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3Zm0 0v7" />
  </svg>
);

const BudgetComparison: React.FC<BudgetComparisonProps> = ({
  fourYearTotal,
  currentYearBudget,
}) => {
  // Calculate tangible object comparisons based on the budget
  const budgetForComparison =
    fourYearTotal || (currentYearBudget ? currentYearBudget : 0);

  // Costs in THB
  const PICKUP_TRUCK_COST = 800000;
  const CONCRETE_ROAD_COST_PER_KM = 2500000;
  const SCHOOL_LUNCH_COST = 21;

  const pickupTrucks = Math.floor(budgetForComparison / PICKUP_TRUCK_COST);
  const concreteRoadKm = (
    budgetForComparison / CONCRETE_ROAD_COST_PER_KM
  ).toFixed(1);
  const schoolLunches = Math.floor(budgetForComparison / SCHOOL_LUNCH_COST);

  if (budgetForComparison <= 0) return null;

  return (
    <div className="budget-comparison-container">
      {/* 4-Year Term Total - Big Impact Number */}
      {fourYearTotal && fourYearTotal > 0 && (
        <div className="four-year-total">
          <div className="four-year-label">งบประมาณสะสมตลอดวาระ (4 ปี)</div>
          <div className="four-year-amount">
            {formatNumberWithCommas(fourYearTotal)}{" "}
            <span className="currency">บาท</span>
          </div>
        </div>
      )}

      {/* Tangible Object Comparison Section */}
      <div className="value-comparison-card">
        <div className="comparison-header">
          <span className="comparison-title">เทียบมูลค่า</span>
          <span className="comparison-subtitle">
            {fourYearTotal
              ? "งบ 4 ปี เทียบเท่ากับ..."
              : "งบประมาณปีนี้เทียบเท่ากับ..."}
          </span>
        </div>
        <div className="comparison-items">
          <div className="comparison-item">
            <div className="comparison-icon truck">
              <TruckIcon />
            </div>
            <div className="comparison-text">
              <span className="comparison-value">
                {formatNumberWithCommas(pickupTrucks)}
              </span>
              <span className="comparison-label">
                ซื้อรถกระบะได้ {formatNumberWithCommas(pickupTrucks)} คัน
              </span>
            </div>
          </div>
          <div className="comparison-item">
            <div className="comparison-icon road">
              <RoadIcon />
            </div>
            <div className="comparison-text">
              <span className="comparison-value">{concreteRoadKm}</span>
              <span className="comparison-label">
                สร้างถนนคอนกรีตได้ {concreteRoadKm} กม.
              </span>
            </div>
          </div>
          <div className="comparison-item">
            <div className="comparison-icon lunch">
              <UtensilsIcon />
            </div>
            <div className="comparison-text">
              <span className="comparison-value">
                {formatNumberWithCommas(schoolLunches)}
              </span>
              <span className="comparison-label">
                อาหารกลางวันเด็ก {formatNumberWithCommas(schoolLunches)} มื้อ
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BudgetComparison;
